import React, { useEffect, useRef } from "react";
import io from "socket.io-client";
import { FitAddon } from "@xterm/addon-fit";
import { Terminal } from "@xterm/xterm";
import "@xterm/xterm/css/xterm.css";
import { useTheme, Box } from "@mui/material";
import useStyles from "./styles";

// Extrai o host da variável de ambiente
const baseUrl =
  window?.config?.REACT_APP_BASE_URL || process.env.REACT_APP_BASE_URL;

const socketUrl = `${baseUrl}/interno`;

export default function XtermConsole({ hostId, token, socketParams }) {
  const theme = useTheme();
  const styles = useStyles(theme);
  // Refs para instâncias (evita re-render)
  const terminalRef = useRef(null);
  const xtermInstance = useRef(null);
  const socketInstance = useRef(null);
  const fitAddonInstance = useRef(null);
  const resizeObserverInstance = useRef(null);

  useEffect(() => {
    if (!hostId || !terminalRef.current) return;

    // Limpa conteúdo anterior para evitar duplicidade visual
    terminalRef.current.innerHTML = "";

    // 1. Configuração do Terminal
    const terminal = new Terminal({
      cursorBlink: true,
      scrollback: 10000, // Histórico maior para rolagem
      allowTransparency: true,
      theme: styles.terminalTheme,
      //fontFamily: '"Fira Code", monospace',
      fontSize: 14,
    });

    const fitAddon = new FitAddon();
    terminal.loadAddon(fitAddon);

    // Salva referências
    xtermInstance.current = terminal;
    fitAddonInstance.current = fitAddon;

    // Monta o terminal
    terminal.open(terminalRef.current);

    // Ajuste inicial de tamanho
    setTimeout(() => {
      try {
        fitAddon.fit();
      } catch (e) {
        console.error("Erro ajuste inicial:", e);
      }
    }, 50);

    // 2. Configuração do Socket
    const newSocket = io(socketUrl, {
      path: "/socket",
      transports: ["websocket"],
      auth: {
        token: token || JSON.parse(localStorage.getItem("user"))?.token,
        logsLoginId: JSON.parse(localStorage.getItem("logs"))?.id,
      },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      ...socketParams,
    });
    socketInstance.current = newSocket;

    // 3. Eventos do Socket
    newSocket.on("connect", () => {
      terminal.write(
        `\r\n\x1b[32m✔ Conectado (Socket ID: ${newSocket.id})\x1b[m\r\n`,
      );
      terminal.write(`\r\n\x1b[34m➜ Iniciando SSH: ${hostId}...\x1b[m\r\n`);

      newSocket.emit("term-connect", { hostId });

      // Reajusta após conexão para garantir sincronia
      setTimeout(() => {
        fitAddon.fit();
        newSocket.emit("term-resize", {
          cols: terminal.cols,
          rows: terminal.rows,
        });
      }, 200);
    });

    newSocket.on("term-output", (data) => terminal.write(data));

    newSocket.on("term-error", (msg) => {
      terminal.write(`\r\n\x1b[31m✖ Erro: ${msg}\x1b[m\r\n`);
    });

    newSocket.on("term-session-killed", () => {
      terminal.write(
        "\r\n\r\n\x1b[41;37m SESSÃO ENCERRADA \x1b[m\r\n\x1b[31mConectado em outra aba/dispositivo.\x1b[m\r\n",
      );
      terminal.options.disableStdin = true; // Bloqueia entrada via API do xterm
      newSocket.disconnect();
    });

    newSocket.on("disconnect", () => {
      terminal.write("\r\n\x1b[33m⚠ Desconectado do servidor\x1b[m\r\n");
    });

    // Envia dados do terminal para o socket
    terminal.onData((data) => newSocket.emit("term-input", data));

    // 4. Observador de Redimensionamento
    let resizeTimeout;
    const observer = new ResizeObserver(() => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (!xtermInstance.current || !socketInstance.current) return;
        try {
          fitAddon.fit();
          socketInstance.current.emit("term-resize", {
            cols: xtermInstance.current.cols,
            rows: xtermInstance.current.rows,
          });
        } catch (e) {
          console.error("Erro resize:", e);
        }
      }, 100);
    });

    observer.observe(terminalRef.current);
    resizeObserverInstance.current = observer;

    // 5. Desmontar
    return () => {
      if (resizeObserverInstance.current)
        resizeObserverInstance.current.disconnect();
      if (socketInstance.current) socketInstance.current.disconnect();
      if (xtermInstance.current) xtermInstance.current.dispose();
    };
  }, [hostId, token]);

  // Atualiza o tema do terminal quando o palette mode muda (sem recriar o terminal)
  useEffect(() => {
    if (!xtermInstance.current) return;
    xtermInstance.current.options.theme = styles.terminalTheme;
  }, [styles.terminalTheme]);

  return (
    <Box sx={{ ...styles.terminalContainer, ...styles.xtermCustom }}>
      <div
        ref={terminalRef}
        style={styles.terminalDiv}
        className="xterm-custom-container"
      />
    </Box>
  );
}
